import type { DryRunResult } from "@/features/people/schemas/import.schema";

/**
 * Pure read-model for the import preview. The dry run is the only moment a
 * reader can still change their mind, so this turns the shore's report into
 * what they need to decide: how many rows will land, which rows will not and
 * why, and what the HR / payroll / compensation / bank handlers will write.
 */

/// Handler keys the shore registers with its ImportExtensionRegistry.
const EXTENSION_LABELS: Readonly<Record<string, string>> = {
  hr: "HR profiles",
  payroll: "Payroll",
  compensation: "Compensation",
  bank_statutory: "Bank and statutory details",
};

export interface ExtensionCountLine {
  readonly key: string;
  readonly label: string;
  readonly counts: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly value: number;
  }>;
}

/// "profilesToCreate" -> "profiles to create". Handler counts are free-form
/// camelCase, so an unknown one still reads as words rather than as code.
export function humanizeCountKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase();
}

/// HR counts for lookups the import creates on the fly, like departments.
/// They read as "what new things appear in settings" rather than as HR
/// profile writes, so the preview lists them beside the new departments.
const CREATED_LOOKUPS: ReadonlyArray<{ key: string; singular: string; plural: string }> = [
  { key: "designationsToCreate", singular: "designation", plural: "designations" },
  { key: "gradesToCreate", singular: "grade", plural: "grades" },
];

/// "3 new designations will be created", one line per lookup with a count.
export function describeCreatedLookups(extensionCounts: DryRunResult["extensionCounts"]): string[] {
  const hr = extensionCounts.hr ?? {};
  return CREATED_LOOKUPS.flatMap(({ key, singular, plural }) => {
    const count = hr[key] ?? 0;
    if (count <= 0) return [];
    return [`${count} new ${count === 1 ? singular : plural} will be created`];
  });
}

/// One line per handler that will write something. Zero counts and handlers
/// with nothing to do are dropped: "0 holds to write" is noise on a people file.
/// Created lookups are dropped too; describeCreatedLookups says them.
export function describeExtensionCounts(
  extensionCounts: DryRunResult["extensionCounts"],
): ExtensionCountLine[] {
  const lines: ExtensionCountLine[] = [];
  for (const [key, counts] of Object.entries(extensionCounts)) {
    const nonZero = Object.entries(counts)
      .filter(
        ([countKey, value]) =>
          value > 0 && !(key === "hr" && CREATED_LOOKUPS.some((lookup) => lookup.key === countKey)),
      )
      .map(([countKey, value]) => ({ key: countKey, label: humanizeCountKey(countKey), value }));
    if (nonZero.length === 0) continue;
    lines.push({ key, label: EXTENSION_LABELS[key] ?? humanizeCountKey(key), counts: nonZero });
  }
  return lines;
}

/// Rows the commit will actually write. Error rows are skipped by the shore,
/// and skip_no_changes rows have nothing to write.
export function importableRowCount(dry: DryRunResult): number {
  return dry.classifications.new + dry.classifications.update_existing;
}

/// Error rows are listed in full up to this many; beyond it the preview says
/// how many more there are. A 2,000-row file with a bad column should not
/// render 2,000 table rows inside a dialog (performance P3).
export const MAX_LISTED_ERROR_ROWS = 50;

export function errorRowsToList(dry: DryRunResult): {
  rows: DryRunResult["rows"];
  hidden: number;
} {
  const errors = dry.rows.filter((row) => row.classification === "error");
  return {
    rows: errors.slice(0, MAX_LISTED_ERROR_ROWS),
    hidden: Math.max(0, errors.length - MAX_LISTED_ERROR_ROWS),
  };
}

type DryRunRow = DryRunResult["rows"][number];

/// A row that will still import but with one or more fields skipped. Error
/// rows are skipped whole, so any warnings on them add nothing.
function importsWithSkippedFields(row: DryRunRow): boolean {
  return row.classification !== "error" && (row.warnings?.length ?? 0) > 0;
}

export function warningRowCount(dry: DryRunResult): number {
  return dry.rows.filter(importsWithSkippedFields).length;
}

/// Same cap as the error list, for the same reason: one bad column on a
/// large file would otherwise warn on every row.
export function warningRowsToList(dry: DryRunResult): {
  rows: DryRunResult["rows"];
  hidden: number;
} {
  const warned = dry.rows.filter(importsWithSkippedFields);
  return {
    rows: warned.slice(0, MAX_LISTED_ERROR_ROWS),
    hidden: Math.max(0, warned.length - MAX_LISTED_ERROR_ROWS),
  };
}
